import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, errMsg } from "../../lib/api";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Badge } from "../ui/Badge";
import { Banner } from "../ui/Banner";
import { Field } from "../ui/Field";

type LocationType = "vending" | "warehouse";

type Loc = {
  code: string;
  name: string;
  city: string | null;
  type: LocationType;
  notes: string | null;
  active: boolean;
};

type DeleteResult = {
  code: string;
  deleted: boolean;
  deactivated: boolean;
  reason: string | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
};

const TYPE_LABEL: Record<LocationType, string> = {
  vending: "Vending",
  warehouse: "Magazzino",
};

export function LocationsAdmin({ open, onClose }: Props) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"list" | "create">("list");
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Loc | null>(null);
  const [result, setResult] = useState<DeleteResult | null>(null);

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [type, setType] = useState<LocationType>("vending");

  const { data: locs, isLoading } = useQuery<Loc[]>({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/api/locations")).data,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setMode("list");
    setError(null);
    setConfirmDelete(null);
    setResult(null);
    setCode("");
    setName("");
    setCity("");
    setType("vending");
  }, [open]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["locations"] });

  const createMut = useMutation({
    mutationFn: async () =>
      (
        await api.post("/api/locations", {
          code: code.trim(),
          name: name.trim(),
          city: city.trim() || null,
          type,
        })
      ).data,
    onSuccess: () => {
      invalidate();
      setMode("list");
    },
    onError: (e) => setError(errMsg(e)),
  });

  const patchMut = useMutation({
    mutationFn: async (v: { code: string; body: Record<string, unknown> }) =>
      (await api.patch(`/api/locations/${v.code}`, v.body)).data,
    onSuccess: () => invalidate(),
    onError: (e) => setError(errMsg(e)),
  });

  const deleteMut = useMutation({
    mutationFn: async (target: string) =>
      (await api.delete(`/api/locations/${target}`)).data as DeleteResult,
    onSuccess: (r) => {
      invalidate();
      setConfirmDelete(null);
      setResult(r);
    },
    onError: (e) => {
      setConfirmDelete(null);
      setError(errMsg(e));
    },
  });

  const busy = createMut.isPending || patchMut.isPending || deleteMut.isPending;

  function rename(loc: Loc) {
    const next = window.prompt("Nuovo nome sede", loc.name);
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === loc.name) return;
    setError(null);
    patchMut.mutate({ code: loc.code, body: { name: trimmed } });
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={mode === "create" ? "Nuova sede" : "Gestione sedi"}
        footer={
          mode === "create" ? (
            <>
              <Button variant="ghost" block onClick={() => setMode("list")} disabled={busy}>
                Annulla
              </Button>
              <Button
                variant="primary"
                block
                disabled={busy || code.trim().length < 2 || name.trim().length < 2}
                onClick={() => {
                  setError(null);
                  createMut.mutate();
                }}
              >
                Crea sede
              </Button>
            </>
          ) : (
            <Button variant="primary" block onClick={() => setMode("create")}>
              <Icon name="plus" size={14} /> Nuova sede
            </Button>
          )
        }
      >
        {error && (
          <Banner tone="danger" icon="info" style={{ marginBottom: 12 }}>
            {error}
          </Banner>
        )}

        {mode === "create" ? (
          <div>
            <Field label="Codice" hint="Identificativo univoco · minimo 2 caratteri (es. AVIANO)">
              <input
                className="tcg-input tcg-mono"
                value={code}
                autoFocus
                placeholder="es. AVIANO"
                onChange={(e) => setCode(e.target.value)}
              />
            </Field>
            <Field label="Nome">
              <input
                className="tcg-input"
                value={name}
                placeholder="es. Aviano"
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field label="Città">
              <input
                className="tcg-input"
                value={city}
                placeholder="es. Aviano (PN)"
                onChange={(e) => setCity(e.target.value)}
              />
            </Field>
            <Field label="Tipo">
              <select
                className="tcg-select"
                value={type}
                onChange={(e) => setType(e.target.value as LocationType)}
              >
                <option value="vending">Vending (macchinetta)</option>
                <option value="warehouse">Magazzino (sede)</option>
              </select>
            </Field>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {isLoading && <div style={{ color: "var(--tcg-muted)", fontSize: 13 }}>Caricamento…</div>}
            {locs?.map((l) => (
              <LocationRow
                key={l.code}
                loc={l}
                busy={busy}
                onRename={() => rename(l)}
                onToggleType={() =>
                  patchMut.mutate({
                    code: l.code,
                    body: { type: l.type === "vending" ? "warehouse" : "vending" },
                  })
                }
                onToggleActive={() =>
                  patchMut.mutate({ code: l.code, body: { active: !l.active } })
                }
                onDelete={() => {
                  setError(null);
                  setConfirmDelete(l);
                }}
              />
            ))}
          </div>
        )}
      </Modal>

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        tone="danger"
        title={`Elimina · ${confirmDelete?.name ?? ""}`}
        footer={
          <>
            <Button variant="ghost" block onClick={() => setConfirmDelete(null)} disabled={busy}>
              Annulla
            </Button>
            <Button
              variant="danger"
              block
              disabled={busy}
              onClick={() => confirmDelete && deleteMut.mutate(confirmDelete.code)}
            >
              Elimina
            </Button>
          </>
        }
      >
        <Banner tone="warn" icon="info">
          Se la sede è collegata a un viaggio aperto verrà solo <strong>disattivata</strong> (non
          eliminata) per non orfanizzare la transfer. Lo storico non viene mai toccato.
        </Banner>
      </Modal>

      <Modal
        open={result !== null}
        onClose={() => setResult(null)}
        tone={result?.deleted ? "success" : "warn"}
        title={result?.deleted ? "Sede eliminata" : "Sede disattivata"}
        footer={
          <Button variant="primary" block onClick={() => setResult(null)}>
            Fatto
          </Button>
        }
      >
        <div style={{ fontSize: 13, color: "var(--tcg-ink)", fontWeight: 600 }}>
          {result?.deleted
            ? `La sede "${result?.code}" è stata eliminata definitivamente.`
            : result?.reason ||
              `La sede "${result?.code}" è stata disattivata perché ancora referenziata.`}
        </div>
      </Modal>
    </>
  );
}

function LocationRow({
  loc,
  busy,
  onRename,
  onToggleType,
  onToggleActive,
  onDelete,
}: {
  loc: Loc;
  busy: boolean;
  onRename: () => void;
  onToggleType: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      style={{
        border: "1px solid var(--tcg-border)",
        borderRadius: 14,
        padding: "12px 14px",
        background: loc.active ? "var(--tcg-card)" : "var(--tcg-bg)",
        opacity: loc.active ? 1 : 0.72,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "var(--tcg-ink)" }}>{loc.name}</div>
          <div className="tcg-mono" style={{ fontSize: 10, color: "var(--tcg-muted)", marginTop: 1 }}>
            {loc.code}
            {loc.city ? ` · ${loc.city}` : ""}
          </div>
        </div>
        <Badge tone={loc.type === "vending" ? "navy" : "yellow"}>{TYPE_LABEL[loc.type]}</Badge>
        <Badge tone={loc.active ? "success" : "danger"}>{loc.active ? "ATTIVA" : "OFF"}</Badge>
      </div>

      <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
        <Button variant="secondary" size="small" disabled={busy} onClick={onRename}>
          <Icon name="tag" size={12} /> Rinomina
        </Button>
        <Button variant="secondary" size="small" disabled={busy} onClick={onToggleType}>
          <Icon name="sync" size={12} /> → {loc.type === "vending" ? "Magazzino" : "Vending"}
        </Button>
        <Button
          variant={loc.active ? "danger" : "primary"}
          size="small"
          disabled={busy}
          onClick={onToggleActive}
        >
          <Icon name={loc.active ? "x" : "check"} size={12} /> {loc.active ? "Disattiva" : "Attiva"}
        </Button>
        <Button variant="danger" size="small" disabled={busy} onClick={onDelete}>
          <Icon name="flag" size={12} /> Elimina
        </Button>
      </div>
    </div>
  );
}
